import React from "react";
import { useRegDetail } from "shared/context/regDetail";
import { isEmpty } from "shared/utils";
import { useParticipantDetails } from "../participantDetails";
import { LogAnalyticsFunctionType, MyExpAnalyticsContextType, MyExpAnalyticsProviderType } from "./types";
import { useLogAnalyticsEvent } from "./dataloader";
import ApiAnalyticsLogRequest from "../../../../shared/types/ApiAnalyticsLogRequest";
import AnalyticsParamType from "../../../../shared/types/AnalyticsParamType";

const MyExpAnalyticsContext = React.createContext<MyExpAnalyticsContextType>({} as MyExpAnalyticsContextType);

function MyExpAnalyticsProvider(props: MyExpAnalyticsProviderType) {
  const regDetail = useRegDetail();
  const { participantDetails, getEventDetailsByEventParticipant, getProgramDetails, getEventDetails } =
    useParticipantDetails();
  const { mutateAsync: logAnalyticsEvent } = useLogAnalyticsEvent();

  const logEvent: LogAnalyticsFunctionType = React.useCallback(
    (eventName, properties, eventType) => {
      try {
        const eventParticipantId =
          properties.eventParticipantId ||
          (properties.programParticipantId && getEventDetails(properties.programParticipantId)?.eventParticipant?.id);
        const eventId =
          eventParticipantId && !properties.eventId
            ? getEventDetailsByEventParticipant(eventParticipantId)?.id
            : properties.eventId;
        const programId =
          properties.programParticipantId && !properties.programId
            ? getProgramDetails(properties.programParticipantId)?.id
            : properties.programId;

        const params: AnalyticsParamType = {
          ...properties,
          eventId,
          programId,
          eventParticipantId,
          regKey: regDetail.key,
          registrationId: regDetail.id,
          registrationParticipantId: participantDetails?.registrationParticipantId
        };
        const reqObj: ApiAnalyticsLogRequest = {
          eventName: eventName,
          trackingInformation: params,
          datastoreType: eventType
        };
        logAnalyticsEvent(reqObj);
      } catch (e) {}
    },
    [
      participantDetails?.registrationParticipantId,
      regDetail.key,
      regDetail.id,
      logAnalyticsEvent,
      getEventDetailsByEventParticipant,
      getProgramDetails,
      getEventDetails
    ]
  );

  return <MyExpAnalyticsContext.Provider value={{ logEvent }}>{props.children}</MyExpAnalyticsContext.Provider>;
}

function useMyExpAnalytics() {
  const context = React.useContext(MyExpAnalyticsContext);
  if (isEmpty(context)) {
    throw new Error(`useMyExpAnalytics must be used within a MyExpAnalyticsProvider`);
  }
  return context;
}

export { MyExpAnalyticsProvider, useMyExpAnalytics };
